import { isNative, nativePlatform } from '../native.ts';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { disablePush, enablePush, pushState, type PushState } from '../push.ts';
import type { MeDTO, PasskeyDTO } from '@tc/contracts';
import { addPasskey, cancelled, listPasskeys, passkeysSupported, removePasskey } from '../passkeys.ts';
import { READING_LOCALES, UI_LOCALES } from '@tc/domain';
import { api, ApiError } from '../api.ts';
import { deleteAccount, logout, setMe, useStore } from '../store.ts';
import { PrivacyNoticeText } from './PrivacyNotice.tsx';
import { languageName, useT, useUiLocale } from '../i18n.ts';
import { Dialog } from './Dialog.tsx';
import { Seal } from './common.tsx';
import { getTheme, setTheme, type ThemeChoice } from '../theme.ts';
import { chime, setSoundEnabled, soundEnabled } from '../alerts.ts';
import { desktop, desktopNotifyEnabled, setDesktopNotifyEnabled } from '../desktop.ts';
import qrcode from 'qrcode-generator';
import { fmtBytes } from '../attachments.ts';
import { appVersion, webCommit } from '../version.ts';
import { disableFaceId, enableFaceId, faceIdKind, faceIdOn, LOCK_CHOICES, lockAfterMinutes, setLockAfterMinutes } from '../faceid.ts';

export type SettingsTab = 'profile' | 'chat' | 'notifications' | 'appearance' | 'security' | 'privacy' | 'apps';

function TabIcon({ d }: { d: string }) {
  return (
    <svg className="tab-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={d} />
    </svg>
  );
}

export function SettingsDialog({ onClose, initialTab }: { onClose: () => void; initialTab?: SettingsTab }) {
  const t = useT();
  const me = useStore((s) => s.me)!;
  const [name, setName] = useState(me.displayName);
  const [saved, setSaved] = useState(false);
  const [pw, setPw] = useState({ currentPassword: '', newPassword: '' });
  const [pwMsg, setPwMsg] = useState<string | null>(null);
  const privacy = useStore((s) => s.privacy);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [del, setDel] = useState<{ open: boolean; password: string; error: string | null }>({ open: false, password: '', error: null });
  // Phones start on the list of categories; wider screens show the first category next to the list.
  const [tab, setTab] = useState<SettingsTab | null>(() => initialTab ?? (window.matchMedia('(max-width: 640px)').matches ? null : 'profile'));

  const patch = async (body: Partial<MeDTO>) => {
    const updated = await api<MeDTO>('PATCH', '/api/me', body);
    setMe(updated);
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  };

  const changePw = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api('POST', '/api/me/password', pw);
      setPwMsg(t('passwordChanged'));
      setPw({ currentPassword: '', newPassword: '' });
    } catch (err) {
      setPwMsg(err instanceof ApiError && err.code === 'invalid_credentials' ? t('invalidCredentials') : t('passwordHint'));
    }
  };

  const tabs: Array<{ id: SettingsTab; label: string; icon: ReactNode }> = [
    { id: 'profile', label: t('profile'), icon: <TabIcon d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4.5 20a7.5 7.5 0 0 1 15 0" /> },
    { id: 'chat', label: t('settingsChat'), icon: <TabIcon d="M4 5h16v11H9l-5 4zM8 9h8M8 12h5" /> },
    { id: 'notifications', label: t('notifications'), icon: <TabIcon d="M6 16V11a6 6 0 0 1 12 0v5l1.5 2h-15zM10 20a2 2 0 0 0 4 0" /> },
    { id: 'appearance', label: t('appearance'), icon: <TabIcon d="M12 3a9 9 0 1 0 0 18V3z M12 3a9 9 0 0 1 0 18" /> },
    { id: 'security', label: t('settingsSecurity'), icon: <TabIcon d="M12 3l7 3v5c0 4.5-3 8.3-7 10-4-1.7-7-5.5-7-10V6zM9.5 12l1.8 1.8 3.5-3.6" /> },
    { id: 'privacy', label: t('settingsPrivacy'), icon: <TabIcon d="M6 10V8a6 6 0 0 1 12 0v2M5 10h14v10H5zM12 14v3" /> },
    { id: 'apps', label: t('dlShort'), icon: <TabIcon d="M12 4v11M7.5 10.5 12 15l4.5-4.5M5 19h14" /> },
  ];
  const current = tabs.find((x) => x.id === tab);

  return (
    <Dialog title={t('settings')} onClose={onClose} wide>
      <div className={`settings ${tab ? 'has-tab' : ''}`}>
        <nav className="settings-nav" aria-label={t('settings')}>
          {tabs.map((x) => (
            <button key={x.id} data-tab={x.id} className={x.id === tab ? 'on' : ''} aria-current={x.id === tab ? 'page' : undefined} onClick={() => setTab(x.id)}>
              {x.icon}
              <span>{x.label}</span>
              <svg className="chev" width="8" height="14" viewBox="0 0 8 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M1.5 1.5 6.5 7l-5 5.5" /></svg>
            </button>
          ))}
          <div className="settings-signout">
            <button className="btn small" onClick={() => void logout(false)}>{t('signOut')}</button>
            <button className="btn danger-outline small" onClick={() => void logout(true)}>{t('signOutEverywhere')}</button>
            <VersionLine />
          </div>
        </nav>
        <div className="settings-content">
          {current && (
            <button className="settings-back link-btn" onClick={() => setTab(null)}>
              ‹ {t('settings')}
            </button>
          )}
          {tab === 'profile' && (
      <section className="stack">
        <h3>{t('profile')}</h3>
        <form
          className="row"
          onSubmit={(e) => {
            e.preventDefault();
            void patch({ displayName: name });
          }}
        >
          <input value={name} maxLength={80} onChange={(e) => setName(e.target.value)} aria-label={t('displayName')} />
          <button className="btn small" disabled={!name.trim() || name === me.displayName}>{t('save')}</button>
        </form>
        <p className="muted small">{me.email}{me.organizationName ? `, ${me.organizationName}` : ''}</p>
        <label className="field">
          <span>{t('uiLanguage')}</span>
          <select value={me.uiLocale} onChange={(e) => void patch({ uiLocale: e.target.value })}>
            {UI_LOCALES.map((l) => <option key={l} value={l}>{languageName(l, l)}</option>)}
          </select>
        </label>
        {saved && <p className="ok small" role="status">{t('saved')}</p>}
      </section>
          )}
          {tab === 'chat' && (
      <section className="stack">
        <h3>{t('settingsChat')}</h3>
        <fieldset className="field">
          <legend>{t('readingLanguage')}</legend>
          <div className="lang-choices">
            {READING_LOCALES.map((l) => (
              <label key={l} className={`lang-choice ${me.readingLocale === l ? 'on' : ''}`}>
                <input type="radio" name="reading" checked={me.readingLocale === l} onChange={() => void patch({ readingLocale: l })} />
                <Seal locale={l} />
                <span>{languageName(me.uiLocale, l)}</span>
              </label>
            ))}
          </div>
          <small className="muted">{t('readingLanguageHint')}</small>
        </fieldset>
        <label className="check">
          <input type="checkbox" checked={me.enterToSend} onChange={(e) => void patch({ enterToSend: e.target.checked })} />
          <span>{t('enterToSend')}</span>
        </label>
        <fieldset className="field">
          <legend>{t('textCheckTitle')}</legend>
          {(['ask', 'auto', 'off'] as const).map((v) => (
            <label key={v} className="check">
              <input type="radio" name="textCheck" checked={me.textCheck === v} onChange={() => void patch({ textCheck: v })} />
              <span>{t(v === 'ask' ? 'textCheckAsk' : v === 'auto' ? 'textCheckAuto' : 'textCheckOff')}</span>
            </label>
          ))}
          <small className="muted">{t('textCheckHint')}</small>
        </fieldset>
        {saved && <p className="ok small" role="status">{t('saved')}</p>}
      </section>
          )}
          {tab === 'notifications' && <NotificationSettings notifyEmail={me.notifyEmail} onEmail={(v) => void patch({ notifyEmail: v })} />}
          {tab === 'appearance' && <ThemeSettings />}
          {tab === 'security' && (
            <>
      <section className="stack">
        <h3>{t('changePassword')}</h3>
        <form className="stack" onSubmit={changePw}>
          <input type="password" placeholder={t('currentPassword')} aria-label={t('currentPassword')} autoComplete="current-password" value={pw.currentPassword} onChange={(e) => setPw({ ...pw, currentPassword: e.target.value })} required />
          <input type="password" placeholder={t('newPassword')} aria-label={t('newPassword')} autoComplete="new-password" minLength={10} value={pw.newPassword} onChange={(e) => setPw({ ...pw, newPassword: e.target.value })} required />
          <button className="btn small">{t('changePassword')}</button>
          {pwMsg && <p className="small" role="status">{pwMsg}</p>}
        </form>
      </section>
              <FaceIdSettings />
              <PasskeySettings />
              <MfaSettings />
            </>
          )}
          {tab === 'privacy' && (
            <>
      <section className="stack">
        <a className="btn small" href="/api/me/export" download>{t('exportData')}</a>
        <p className="muted small">{t('exportHint')}</p>
      </section>
      <section className="stack">
        <h3>{t('privacy')}</h3>
        <button className="btn small" onClick={() => setShowPrivacy((v) => !v)} aria-expanded={showPrivacy}>{t('privacyShow')}</button>
        {showPrivacy && privacy && <div className="notice-box"><PrivacyNoticeText info={privacy} uiLocale={me.uiLocale} /></div>}
        {!del.open ? (
          <button className="btn danger-outline small" onClick={() => setDel({ ...del, open: true })}>{t('deleteAccount')}</button>
        ) : (
          <form
            className="stack danger-zone"
            onSubmit={async (e) => {
              e.preventDefault();
              try {
                await deleteAccount(del.password);
              } catch (err) {
                setDel({ ...del, error: err instanceof ApiError && err.code === 'invalid_credentials' ? t('invalidCredentials') : t('error_generic', { code: err instanceof ApiError ? err.code : 'network' }) });
              }
            }}
          >
            <p className="small">{t('deleteAccountHint')}</p>
            <input type="password" required placeholder={t('password')} aria-label={t('password')} autoComplete="current-password" value={del.password} onChange={(e) => setDel({ ...del, password: e.target.value })} />
            {del.error && <p className="form-error" role="alert">{del.error}</p>}
            <div className="row">
              <button className="btn danger-outline small" disabled={!del.password}>{t('deleteAccountConfirm')}</button>
              <button type="button" className="link-btn" onClick={() => setDel({ open: false, password: '', error: null })}>{t('cancel')}</button>
            </div>
          </form>
        )}
      </section>
            </>
          )}
          {tab === 'apps' && <AppDownloads showEmpty />}
        </div>
      </div>
    </Dialog>
  );
}

export interface DownloadItem {
  name: string;
  platform: 'mac-arm64' | 'mac-x64' | 'windows' | 'windows-arm64' | 'android';
  version: string;
  byteSize: number;
}

/** The apps for computer and phone (newest version of each), downloaded with the user's session. */
/**
 * Which installer fits this device. Chrome/Edge tell the processor type; Safari does not, but only Apple's
 * own chips have the ASTC texture format in WebGL, which tells Apple silicon from Intel.
 */
export async function detectPlatform(): Promise<DownloadItem['platform'] | null> {
  const ua = navigator.userAgent;
  if (/Android/i.test(ua)) return 'android';
  if (/iPhone|iPad/i.test(ua)) return null;
  const isWin = /Windows/i.test(ua);
  const isMac = /Mac OS X|Macintosh/i.test(ua);
  if (!isWin && !isMac) return null;
  let arch: 'arm' | 'x86' | null = null;
  const uad = (navigator as Navigator & { userAgentData?: { getHighEntropyValues(h: string[]): Promise<{ architecture?: string }> } }).userAgentData;
  if (uad) {
    const v = await uad.getHighEntropyValues(['architecture']).catch(() => ({ architecture: undefined }));
    if (v.architecture === 'arm' || v.architecture === 'x86') arch = v.architecture;
  }
  if (!arch && isMac) {
    try {
      const gl = document.createElement('canvas').getContext('webgl');
      if (gl) arch = gl.getSupportedExtensions()?.includes('WEBGL_compressed_texture_astc') ? 'arm' : 'x86';
    } catch {
      /* unknown: fall through */
    }
  }
  if (isWin) return arch === 'arm' ? 'windows-arm64' : 'windows';
  return arch === 'x86' ? 'mac-x64' : 'mac-arm64';
}

export function AppDownloads({ showEmpty = false }: { showEmpty?: boolean }) {
  const t = useT();
  const [items, setItems] = useState<DownloadItem[] | null>(null);
  const [here, setHere] = useState<DownloadItem['platform'] | null>(null);
  useEffect(() => {
    api<{ items: DownloadItem[] }>('GET', '/api/downloads').then((r) => setItems(r.items)).catch(() => setItems([]));
    void detectPlatform().then(setHere);
  }, []);
  if (!items) return null;
  const iphone = /iPhone|iPad/i.test(navigator.userAgent);
  if (!items.length && !iphone && !showEmpty) return null;
  const label = { 'mac-arm64': t('dlMacArm'), 'mac-x64': t('dlMacIntel'), windows: 'Windows', 'windows-arm64': t('dlWinArm'), android: 'Android' } as const;
  const hint = { 'mac-arm64': '', 'mac-x64': '', windows: t('dlWindowsHint'), 'windows-arm64': t('dlWinArmHint'), android: t('dlAndroidHint') } as const;
  const mine = items.find((d) => d.platform === here);
  const others = items.filter((d) => d !== mine);
  const row = (d: DownloadItem, primary: boolean) => (
    <li key={d.name} className={primary ? 'mine' : ''}>
      <div>
        <strong>{label[d.platform]}</strong> <span className="muted small">{d.version} · {fmtBytes(d.byteSize)}</span>
        {hint[d.platform] && <div className="muted small">{hint[d.platform]}</div>}
      </div>
      <a className={`btn small ${primary ? 'primary' : ''}`} href={`/api/downloads/${encodeURIComponent(d.name)}`} download>
        {t('dlButton')}
      </a>
    </li>
  );
  return (
    <section className="stack" aria-labelledby="apps-h">
      <h3 id="apps-h">{t('dlTitle')}</h3>
      <p className="muted small">{t('dlIntro')}</p>
      {!items.length && !iphone && <p className="muted small">{t('dlNone')}</p>}
      {iphone && (
        <ul className="downloads">
          <li className="mine">
            <div>
              <strong>iPhone</strong>
              <div className="muted small">{t('dlIphoneHint')}</div>
            </div>
          </li>
        </ul>
      )}
      {mine && (
        <>
          <p className="small"><strong>{t('dlForThis')}</strong></p>
          <ul className="downloads">{row(mine, true)}</ul>
        </>
      )}
      {others.length > 0 &&
        (mine || iphone ? (
          <details className="dl-others">
            <summary>{t('dlOthers')}</summary>
            <ul className="downloads">{others.map((d) => row(d, false))}</ul>
          </details>
        ) : (
          <ul className="downloads">{others.map((d) => row(d, false))}</ul>
        ))}
    </section>
  );
}

function NotificationSettings({ notifyEmail, onEmail }: { notifyEmail: boolean; onEmail: (v: boolean) => void }) {
  const t = useT();
  const [state, setState] = useState<PushState | null>(null);
  const [busy, setBusy] = useState(false);
  const [emailAvailable, setEmailAvailable] = useState(true);
  const [failed, setFailed] = useState(false);
  const [sound, setSound] = useState(soundEnabled());
  useEffect(() => {
    void pushState().then(setState);
    api<{ email: boolean }>('GET', '/api/push/key').then((r) => setEmailAvailable(r.email)).catch(() => {});
  }, []);
  const toggle = async () => {
    setBusy(true);
    setFailed(false);
    try {
      setState(state === 'on' ? await disablePush() : await enablePush());
    } catch {
      setFailed(state !== 'on');
      setState(await pushState());
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="stack">
      <h3>{t('notifications')}</h3>
      {desktop ? (
        <DesktopNotifyToggle />
      ) : state === 'on' || state === 'off' ? (
        <div className="row">
          <button className={`btn small ${state === 'off' ? 'primary' : ''}`} disabled={busy} onClick={() => void toggle()}>
            {state === 'on' ? t('pushOff') : t('pushOn')}
          </button>
          <span className="muted small">{state === 'on' ? t('pushIsOn') : t('pushIsOff')}</span>
        </div>
      ) : state ? (
        <p className="muted small">{t(state === 'needs-install' ? 'pushNeedsInstall' : state === 'denied' ? 'pushDenied' : 'pushUnsupported')}</p>
      ) : null}
      {failed && <p className="form-error small" role="alert">{t('pushFailed')}</p>}
      <div className="row">
        <label className="check">
          <input
            type="checkbox"
            checked={sound}
            onChange={(e) => {
              setSound(e.target.checked);
              setSoundEnabled(e.target.checked);
              if (e.target.checked) chime('message', true);
            }}
          />
          <span>{t('soundNewMessages')}</span>
        </label>
        <button type="button" className="link-btn small" onClick={() => chime('mention', true)}>{t('soundTest')}</button>
      </div>
      <label className="check">
        <input type="checkbox" checked={notifyEmail} onChange={(e) => onEmail(e.target.checked)} />
        <span>{t('emailReminders')}</span>
      </label>
      {!emailAvailable && <p className="muted small">{t('emailNotConfigured')}</p>}
      <p className="muted small">{t('notificationsHint')}</p>
    </section>
  );
}

function ThemeSettings() {
  const t = useT();
  const [choice, setChoice] = useState<ThemeChoice>(getTheme());
  const pick = (c: ThemeChoice) => {
    setChoice(c);
    setTheme(c);
  };
  return (
    <section className="stack">
      <h3 id="theme-h">{t('appearance')}</h3>
      <div className="segmented" role="radiogroup" aria-labelledby="theme-h">
        {(['system', 'light', 'dark'] as const).map((c) => (
          <button key={c} type="button" role="radio" aria-checked={choice === c} className={choice === c ? 'on' : ''} onClick={() => pick(c)}>
            {t(c === 'system' ? 'themeSystem' : c === 'light' ? 'themeLight' : 'themeDark')}
          </button>
        ))}
      </div>
      <small className="muted">{t('themeHint')}</small>
    </section>
  );
}

/** iPhone app: Face ID instead of the password (faceid.ts). */
export function FaceIdSettings() {
  const t = useT();
  const [state, setState] = useState<{ kind: 'face' | 'touch' | 'android'; on: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [lock, setLock] = useState(lockAfterMinutes);
  useEffect(() => {
    void faceIdKind().then(async (kind) => kind && setState({ kind, on: await faceIdOn() }));
  }, []);
  if (!state) return null;
  const toggle = async (on: boolean) => {
    setBusy(true);
    setFailed(false);
    try {
      if (on) await enableFaceId();
      else await disableFaceId();
      setState({ ...state, on: await faceIdOn() });
    } catch {
      setFailed(on);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="stack" aria-labelledby="faceid-h">
      <h3 id="faceid-h">{state.kind === 'touch' ? 'Touch ID' : state.kind === 'android' ? t('bioTitle') : 'Face ID'}</h3>
      <p className="muted small">{state.kind === 'android' ? t('bioIntro') : t('faceIdIntro')}</p>
      <label className="check">
        <input type="checkbox" checked={state.on} disabled={busy} onChange={(e) => void toggle(e.target.checked)} />
        <span>{state.kind === 'android' ? t('bioToggle') : t('faceIdToggle')}</span>
      </label>
      {failed && <p className="form-error small" role="alert">{t('faceIdFailed')}</p>}
      {state.on && (
        <label className="field">
          <span>{t('lockAfter')}</span>
          <select value={lock} onChange={(e) => (setLockAfterMinutes(Number(e.target.value)), setLock(Number(e.target.value)))}>
            {LOCK_CHOICES.map((m) => (
              <option key={m} value={m}>
                {m < 0 ? t('lockNever') : m === 0 ? t('lockNow') : m === 60 ? t('lockHour') : t('lockMinutes', { n: m })}
              </option>
            ))}
          </select>
        </label>
      )}
    </section>
  );
}

function PasskeySettings() {
  const t = useT();
  const ui = useUiLocale();
  const [keys, setKeys] = useState<PasskeyDTO[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const supported = passkeysSupported();
  useEffect(() => {
    listPasskeys().then(setKeys).catch(() => setKeys([]));
  }, []);
  const add = async () => {
    setBusy(true);
    setError(false);
    try {
      const k = await addPasskey();
      setKeys((ks) => [...(ks ?? []), k]);
    } catch (err) {
      if (!cancelled(err)) setError(true);
    } finally {
      setBusy(false);
    }
  };
  const fmt = (iso: string) => new Date(iso).toLocaleDateString(ui === 'sv-SE' ? 'sv-SE' : ui === 'zh-Hans' ? 'zh-CN' : 'en-GB', { dateStyle: 'medium' });
  return (
    <section className="stack" aria-labelledby="passkeys-h">
      <h3 id="passkeys-h">{t('passkeys')}</h3>
      <p className="muted small">{t('passkeysIntro')}</p>
      {keys && keys.length > 0 && (
        <ul className="passkey-list">
          {keys.map((k) => (
            <li key={k.id}>
              <span>
                {k.name}
                {k.synced && <span className="tag">{t('passkeySynced')}</span>}
                <span className="muted small"> · {k.lastUsedAt ? t('passkeyLastUsed', { when: fmt(k.lastUsedAt) }) : t('passkeyNever')}</span>
              </span>
              <button className="link-btn danger" onClick={() => void removePasskey(k.id).then(() => setKeys((ks) => (ks ?? []).filter((x) => x.id !== k.id)))}>
                {t('removePasskey')}
              </button>
            </li>
          ))}
        </ul>
      )}
      {supported ? (
        <button className="btn small" onClick={() => void add()} disabled={busy}>{t('addPasskey')}</button>
      ) : (
        <p className="muted small">{t(desktop || isNative ? 'passkeysDesktop' : 'passkeysUnsupported')}</p>
      )}
      {error && <p className="form-error small" role="alert">{t('passkeyInvalid')}</p>}
    </section>
  );
}

/** In the desktop app notifications come from the app itself (no push service), so this is just on/off. */
function DesktopNotifyToggle() {
  const t = useT();
  const [on, setOn] = useState(desktopNotifyEnabled());
  return (
    <label className="check">
      <input
        type="checkbox"
        checked={on}
        onChange={(e) => {
          setOn(e.target.checked);
          setDesktopNotifyEnabled(e.target.checked);
        }}
      />
      <span>{t('desktopNotify')}</span>
    </label>
  );
}

/** Two-step sign-in with an authenticator app. Recommended for admins. */
function MfaSettings() {
  const t = useT();
  const me = useStore((s) => s.me)!;
  const [status, setStatus] = useState<{ enabled: boolean; recoveryCodesLeft: number } | null>(null);
  const [setup, setSetup] = useState<{ secret: string; uri: string } | null>(null);
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [off, setOff] = useState<{ open: boolean; password: string }>({ open: false, password: '' });
  const [error, setError] = useState<string | null>(null);
  const load = () => api<{ enabled: boolean; recoveryCodesLeft: number }>('GET', '/api/me/mfa').then(setStatus).catch(() => {});
  useEffect(() => void load(), []);
  const qr = (uri: string) => {
    const q = qrcode(0, 'M');
    q.addData(uri);
    q.make();
    return q.createDataURL(5, 4);
  };
  const begin = async () => {
    setError(null);
    setSetup(await api<{ secret: string; uri: string }>('POST', '/api/me/mfa/setup', {}));
  };
  const confirm = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const r = await api<{ recoveryCodes: string[] }>('POST', '/api/me/mfa/confirm', { code });
      setCodes(r.recoveryCodes);
      setSetup(null);
      setCode('');
      void load();
    } catch {
      setError(t('mfaCodeInvalid'));
    }
  };
  const disable = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await api('POST', '/api/me/mfa/disable', { password: off.password });
      setOff({ open: false, password: '' });
      void load();
    } catch {
      setError(t('invalidCredentials'));
    }
  };
  const download = () => {
    const blob = new Blob([`T-Lián – ${me.email}\n${t('mfaCodesTitle')}\n\n${codes!.join('\n')}\n`], { type: 'text/plain' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 't-lian-recovery-codes.txt';
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };
  if (!status) return null;
  return (
    <section className="stack" aria-labelledby="mfa-h">
      <h3 id="mfa-h">{t('mfa')}</h3>
      <p className="muted small">{t('mfaIntro')}{me.isAdmin && !status.enabled ? ` ${t('mfaAdminTip')}` : ''}</p>
      {codes ? (
        <div className="stack mfa-codes">
          <p><strong>{t('mfaCodesTitle')}</strong></p>
          <p className="muted small">{t('mfaCodesHint')}</p>
          <ol>{codes.map((c) => <li key={c}><code>{c}</code></li>)}</ol>
          <div className="row wrap">
            <button className="btn small" onClick={download}>{t('download')}</button>
            <button className="btn small primary" onClick={() => setCodes(null)}>{t('mfaCodesSaved')}</button>
          </div>
        </div>
      ) : status.enabled ? (
        <>
          <p className="small">✓ {t('mfaOn')} · {t('mfaCodesLeft', { n: status.recoveryCodesLeft })}</p>
          {!off.open ? (
            <button className="btn small danger-outline" onClick={() => setOff({ open: true, password: '' })}>{t('mfaTurnOff')}</button>
          ) : (
            <form className="row wrap" onSubmit={disable}>
              <input type="password" required placeholder={t('password')} aria-label={t('password')} autoComplete="current-password" value={off.password} onChange={(e) => setOff({ ...off, password: e.target.value })} />
              <button className="btn small danger-outline">{t('mfaTurnOff')}</button>
              <button type="button" className="link-btn" onClick={() => setOff({ open: false, password: '' })}>{t('cancel')}</button>
            </form>
          )}
        </>
      ) : setup ? (
        <form className="stack" onSubmit={confirm}>
          <p className="small">{t('mfaScan')}</p>
          <img className="mfa-qr" src={qr(setup.uri)} alt={t('mfaQrAlt')} width={180} height={180} />
          <p className="muted small">{t('mfaManual')} <code className="mfa-secret">{setup.secret.replace(/(.{4})/g, '$1 ').trim()}</code></p>
          <label className="field">
            <span>{t('mfaCode')}</span>
            <input inputMode="numeric" autoComplete="one-time-code" required maxLength={8} value={code} onChange={(e) => setCode(e.target.value)} />
          </label>
          <div className="row">
            <button className="btn small primary">{t('mfaConfirm')}</button>
            <button type="button" className="link-btn" onClick={() => setSetup(null)}>{t('cancel')}</button>
          </div>
        </form>
      ) : (
        <button className="btn small" onClick={() => void begin()}>{t('mfaTurnOn')}</button>
      )}
      {error && <p className="form-error small" role="alert">{error}</p>}
    </section>
  );
}

/** "App 0.1.4 (29912345) · web 2a9572e": which app and which server version this is (for support). */
function VersionLine() {
  const t = useT();
  const [app, setApp] = useState<string | null>(null);
  useEffect(() => {
    void appVersion().then(setApp);
  }, []);
  return (
    <p className="muted small version-line" data-testid="version">
      {app ? `${t('versionApp')} ${app} · ` : ''}
      {t('versionWeb')} {webCommit}
    </p>
  );
}
